'use client'

// session/context.tsx — React contexts + hooks + global updater.
//
// The single SessionContext value changes on every state mutation, which forces
// every consumer of `useAppSession()` to re-render. To keep frequently mounted
// components (Settings, AppShell, theme provider, route guards) from re-rendering
// on unrelated state changes (HAB-49), the session state is exposed through three
// focused slice contexts in addition to the legacy SessionContext facade:
//
//   - UserContext         → auth identity slice (session.user)
//   - PreferencesContext  → user preferences (theme, a11y, notifications, modules)
//   - SessionMetaContext  → auth/lifecycle meta (hydrated, isLoading, isAuthenticated, …)
//
// Existing fine-grained contexts (Accessibility / NotificationPreferences /
// UserProfile) are kept as-is so already-migrated consumers don't churn. The
// legacy `useAppSession` facade still exposes the full state and the updater for
// callers that genuinely need them (writes, large reads spanning slices).

import { createContext, useCallback, useContext, useRef, useSyncExternalStore } from 'react'
import type { SessionState } from '@shared/types/domain-types'
import { DEFAULT_STATE, type AppSessionState } from './types'
import { sessionStore } from './session-store'

let globalSessionUpdater: ((updater: (state: SessionState) => SessionState) => void) | null = null

export const SessionContext = createContext<{
    session: AppSessionState
    updateAppSessionState: (updater: (state: AppSessionState) => AppSessionState) => void
}>({
    session: DEFAULT_STATE,
    updateAppSessionState: () => { },
})

export const AccessibilityContext = createContext<AppSessionState['accessibility']>(DEFAULT_STATE.accessibility)
export const NotificationPreferencesContext = createContext<AppSessionState['notificationPreferences']>(DEFAULT_STATE.notificationPreferences)
export const UserProfileContext = createContext<AppSessionState['userProfile']>(DEFAULT_STATE.userProfile)

// HAB-49: focused slices.
export type UserSlice = AppSessionState['user']

export interface PreferencesSlice {
    enabledModules: AppSessionState['enabledModules']
    accessibility: AppSessionState['accessibility']
    notificationPreferences: AppSessionState['notificationPreferences']
    activeTheme: AppSessionState['arsenal']['activeTheme']
    activeThemeId: AppSessionState['arsenal']['activeThemeId']
}

export interface SessionMetaSlice {
    hydrated: boolean
    isLoading: boolean
    isAuthenticated: boolean
    activeUserId: AppSessionState['activeUserId']
    firstAccess: boolean
    onboardingCompleted: boolean
    postOnboardingRedirect: AppSessionState['postOnboardingRedirect']
}

const DEFAULT_PREFERENCES: PreferencesSlice = {
    enabledModules: DEFAULT_STATE.enabledModules,
    accessibility: DEFAULT_STATE.accessibility,
    notificationPreferences: DEFAULT_STATE.notificationPreferences,
    activeTheme: DEFAULT_STATE.arsenal.activeTheme,
    activeThemeId: DEFAULT_STATE.arsenal.activeThemeId,
}

const DEFAULT_SESSION_META: SessionMetaSlice = {
    hydrated: DEFAULT_STATE.hydrated,
    isLoading: DEFAULT_STATE.isLoading,
    isAuthenticated: DEFAULT_STATE.isAuthenticated,
    activeUserId: DEFAULT_STATE.activeUserId,
    firstAccess: DEFAULT_STATE.firstAccess,
    onboardingCompleted: DEFAULT_STATE.onboardingCompleted,
    postOnboardingRedirect: DEFAULT_STATE.postOnboardingRedirect,
}

export const UserContext = createContext<UserSlice>(DEFAULT_STATE.user)
export const PreferencesContext = createContext<PreferencesSlice>(DEFAULT_PREFERENCES)
export const SessionMetaContext = createContext<SessionMetaSlice>(DEFAULT_SESSION_META)

export function useAppSession() {
    const { session, updateAppSessionState } = useContext(SessionContext)
    return {
        hydrated: session.hydrated,
        session,
        updateAppSessionState
    }
}

// HAB-142 Phase B: re-implemented on the selector store so normalizeSessionState
// reference churn no longer triggers re-renders for unchanged values.
export function useAccessibility() {
    return useSessionSelector((s) => s.accessibility, shallowEqual)
}

export function useNotificationPreferences() {
    return useSessionSelector((s) => s.notificationPreferences, shallowEqual)
}

export function useUserProfile() {
    return useSessionSelector((s) => s.userProfile, shallowEqual)
}

// HAB-49: focused hooks. Prefer these over `useAppSession()` when a component
// only needs one slice — re-renders are scoped to that slice's data.
export function useUser(): UserSlice {
    return useContext(UserContext)
}

export function usePreferences(): PreferencesSlice {
    return useContext(PreferencesContext)
}

export function useSessionMeta(): SessionMetaSlice {
    return useContext(SessionMetaContext)
}

// HAB-142: selector-level subscriptions.
//
// `useSessionSelector` subscribes a component to a *derived value* of the
// session state and re-renders it only when that value changes per `isEqual`
// (default `Object.is`). Unlike the slice contexts above, it is immune to the
// new object references `normalizeSessionState` produces on every update: a
// consumer of `s => s.accessibility.reduceMotion` re-renders only when that
// boolean flips, regardless of churn elsewhere in the session.
//
// Keep selectors stable (module-scope or `useCallback`) and select the
// narrowest value you need. For object/array selections pass `shallowEqual`
// (or a custom comparator) so equal-by-value results don't re-render.

/** Shallow object equality for object-returning selectors. */
export function shallowEqual<T>(a: T, b: T): boolean {
    if (Object.is(a, b)) return true
    if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false
    const aKeys = Object.keys(a as Record<string, unknown>)
    const bKeys = Object.keys(b as Record<string, unknown>)
    if (aKeys.length !== bKeys.length) return false
    for (const key of aKeys) {
        if (!Object.is((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) {
            return false
        }
    }
    return true
}

export function useSessionSelector<T>(
    selector: (state: AppSessionState) => T,
    isEqual: (a: T, b: T) => boolean = Object.is,
): T {
    const selectorRef = useRef(selector)
    const isEqualRef = useRef(isEqual)
    selectorRef.current = selector
    isEqualRef.current = isEqual

    // Cache the last selection so getSnapshot returns a stable reference between
    // store writes (useSyncExternalStore requirement) and so equal-by-value
    // selections don't trigger a re-render.
    const lastRef = useRef<{ value: T } | null>(null)

    const getSelection = useCallback((): T => {
        const next = selectorRef.current(sessionStore.getSnapshot())
        const cached = lastRef.current
        if (cached !== null && isEqualRef.current(cached.value, next)) {
            return cached.value
        }
        lastRef.current = { value: next }
        return next
    }, [])

    return useSyncExternalStore(sessionStore.subscribe, getSelection, getSelection)
}

// Standalone function for components that import directly
// Note: This won't work outside AppSessionProvider, but provides type compatibility
export function updateAppSessionState(_updater: (state: AppSessionState) => AppSessionState): void {
    if (globalSessionUpdater) {
        globalSessionUpdater(_updater)
        return
    }

    console.warn('updateAppSessionState: AppSessionProvider not ready yet')
}

export function setGlobalSessionUpdater(updater: typeof globalSessionUpdater) {
    globalSessionUpdater = updater
}

export function getGlobalSessionUpdater() {
    return globalSessionUpdater
}
